import React from "react";
import { Play, Film } from "lucide-react";
import Reveal from "@/components/Reveal";
import KineticMarquee from "@/components/KineticMarquee";
import HorizontalGallery from "@/components/HorizontalGallery";
import ServiceCard from "@/components/ServiceCard";
import DirectorButton from "@/components/DirectorButton";
import Isotipo from "@/components/Isotipo";
import Seo from "@/components/Seo";
import { Link } from "react-router-dom";
import { IMG, AUDIOVISUAL_CAPABILITIES, SLATE, PROJECTS, UI } from "@/data/site";
import { SEO } from "@/data/seo";
import { useLang } from "@/context/LanguageContext";

const Audiovisual = () => {
  const { t, lp } = useLang();
  const avProjects = PROJECTS.filter((p) => p.branch === "audiovisual");
  const gallery = [...SLATE, ...avProjects.filter((p) => !SLATE.some((s) => s.slug === p.slug))];

  return (
    <div data-testid="audiovisual-page" className="bg-ink">
      <Seo {...SEO.audiovisual} />
      {/* Cinematic hero */}
      <section className="relative h-screen flex items-center overflow-hidden pt-[72px]">
        <img src={IMG.audiovisualHero} alt="" className="absolute inset-0 w-full h-full object-cover opacity-55 kenburns" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(23,23,38,0.97) 8%, rgba(23,23,38,0.5) 55%, rgba(23,23,38,0.4) 100%)" }} />
        {/* letterbox bars */}
        <div className="absolute top-[72px] inset-x-0 h-10 md:h-16 bg-ink" />
        <div className="absolute bottom-0 inset-x-0 h-10 md:h-16 bg-ink" />
        <Isotipo className="absolute bottom-24 right-[6%] w-32 md:w-56 opacity-25" fill="#edaa51" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
          <Reveal>
            <span className="font-display text-xs font-bold uppercase tracking-[0.35em] text-brand-gold">{t({ es: "Estudio de desarrollo", en: "Development studio" })}</span>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 font-display font-black uppercase text-white leading-[0.85] tracking-tighter" style={{ fontSize: "clamp(3rem, 11vw, 12rem)" }}>
              {t({ es: "Producción", en: "Audiovisual" })}<br /><span className="text-stroke-gold">{t({ es: "Audiovisual", en: "Production" })}</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-2xl text-white/75 font-body text-lg md:text-xl">
              {t({ es: "Documentales, películas, series y programas. Producción propia y coproducción con ambición cinematográfica.", en: "Documentaries, films, series and programs. In-house production and co-production with cinematic ambition." })}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Manifesto */}
      <section className="relative py-24 md:py-40 overflow-hidden">
        <KineticMarquee text="cine · relato · autor · " direction="right" className="absolute top-1/2 -translate-y-1/2 inset-x-0" speed={40} />
        <div className="relative max-w-6xl mx-auto px-6 md:px-12">
          <Reveal>
            <Film className="text-brand-gold mb-8" size={40} />
            <p className="font-display text-3xl md:text-5xl lg:text-6xl font-black uppercase text-white leading-[1.05] text-balance">
              {t({ es: "No producimos solo por encargo. ", en: "We don't just produce to order. " })}
              <span className="text-white/40">
                {t({ es: "Detectamos historias, las escribimos, buscamos coproducción y las llevamos al estreno.", en: "We find stories, write them, line up co-production and take them all the way to the screen." })}
              </span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* Filmmaker full-bleed band */}
      <section className="relative h-[75vh] md:h-[90vh] overflow-hidden grain" data-testid="filmmaker-band">
        <img src={IMG.filmmaker} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70 kenburns-slow" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(23,23,38,0.9) 0%, rgba(23,23,38,0.35) 55%, rgba(23,23,38,0.15) 100%)" }} />
        <div className="absolute top-0 inset-x-0 h-12 md:h-20 bg-ink" />
        <div className="absolute bottom-0 inset-x-0 h-12 md:h-20 bg-ink" />
        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-center">
          <span className="font-display text-xs font-bold uppercase tracking-[0.3em] text-brand-gold mb-6">{t({ es: "Mirada de autor", en: "A filmmaker's eye" })}</span>
          <h2 className="font-display font-black uppercase text-white leading-[0.9] tracking-tight max-w-4xl text-balance" style={{ fontSize: "clamp(2.4rem, 6vw, 6rem)" }}>
            {t({ es: "Pensamos en planos, no en anuncios", en: "We think in shots, not spots" })}
          </h2>
          <p className="mt-6 max-w-xl text-white/70 font-body text-lg">
            {t({ es: "Dirección, fotografía y montaje al servicio de la historia. Del tratamiento de guion a la sala de color.", en: "Direction, cinematography and editing at the service of the story. From script treatment to the color suite." })}
          </p>
        </div>
      </section>

      {/* Behind the scenes strip */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="bts-strip">
        <Reveal>
          <div className="flex items-end justify-between mb-8">
            <h3 className="font-display text-2xl md:text-4xl font-black uppercase text-white">{t({ es: "Detrás de cámaras", en: "Behind the scenes" })}</h3>
            <span className="font-display text-xs font-bold uppercase tracking-widest text-white/40">{t({ es: "El proceso", en: "The process" })}</span>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[IMG.filmSet, IMG.setLighting, IMG.studio, IMG.camera].map((src, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="relative rounded-xl overflow-hidden aspect-[3/4] group">
                <img src={src} alt="" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/70 to-transparent" />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Capabilities */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32">
        <Reveal>
          <span className="font-display text-xs font-bold uppercase tracking-[0.25em] text-brand-gold">{t({ es: "Capacidades", en: "Capabilities" })}</span>
        </Reveal>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {AUDIOVISUAL_CAPABILITIES.map((c, i) => (
            <Reveal key={c.id} delay={i * 0.05}>
              <ServiceCard item={c} index={i} accent="#edaa51" testId={`av-capability-${c.id}`} />
            </Reveal>
          ))}
        </div>
      </section>

      {/* Poster gallery */}
      <section className="py-16 md:py-24 border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 mb-10">
          <Reveal>
            <div className="flex items-end justify-between">
              <div>
                <span className="font-display text-xs font-bold uppercase tracking-[0.25em] text-brand-gold">{t({ es: "Nuestro slate", en: "Our slate" })}</span>
                <h2 className="mt-4 font-display text-4xl md:text-6xl font-black uppercase text-white leading-none">{t({ es: "Producciones", en: "Productions" })}</h2>
              </div>
              <p className="hidden md:block font-display text-xs font-bold uppercase tracking-widest text-white/40">{t({ es: "Desliza →", en: "Swipe →" })}</p>
            </div>
          </Reveal>
        </div>
        <HorizontalGallery items={gallery} />
      </section>

      {/* Featured production with Vimeo */}
      {SLATE.filter((s) => s.vimeo).map((s) => (
        <section key={s.slug} className="max-w-6xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <div className="flex items-center gap-4 mb-6 text-xs font-display font-bold uppercase tracking-widest text-brand-gold">
              <span>{t(s.format)}</span><span className="text-white/30">·</span><span className="text-white/50">{s.year}</span>
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-black uppercase text-white mb-8">{s.title}</h2>
            <div className="relative rounded-2xl overflow-hidden border border-white/10 aspect-video bg-black group">
              <img src={s.image} alt={s.title} className="absolute inset-0 w-full h-full object-cover opacity-60" />
              <Link to={lp(`/proyectos/${s.slug}`)} className="absolute inset-0 flex items-center justify-center" data-testid={`featured-play-${s.slug}`}>
                <span className="w-20 h-20 rounded-full bg-brand-gold/90 text-ink flex items-center justify-center group-hover:scale-110 transition-transform"><Play size={30} className="ml-1" /></span>
              </Link>
            </div>
          </Reveal>
        </section>
      ))}

      {/* CTA */}
      <section className="py-24 md:py-36 border-t border-white/10">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <Reveal>
            <Isotipo className="w-12 h-12 mx-auto mb-8" fill="#edaa51" />
            <h2 className="font-display text-4xl md:text-6xl font-black uppercase text-white leading-[0.95] text-balance">
              {t({ es: "¿Tienes una historia? Desarrollémosla.", en: "Got a story? Let's develop it." })}
            </h2>
            <div className="mt-10 flex justify-center"><DirectorButton to={lp("/contacto")} color="#edaa51" testId="audiovisual-cta">{t(UI.cta.talk)}</DirectorButton></div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Audiovisual;
